<script setup>
  import {ref, watch} from "vue";

  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({name: "ComponentAiRating"});

  const props = defineProps({
    label: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    upOptions: {
      type: Array,
      default: () => [
        {value: 0, label: "Der Text bildet den Inhalt gut ab."},
        {value: 1, label: "Der Text ist Zielgruppen-gerecht formuliert."},
        {value: 2, label: "Die KI-Funktion spart Zeit."},
        {value: "custom", label: "Freitext"},
      ],
    },
    downOptions: {
      type: Array,
      default: () => [
        {value: 0, label: "Der Text enthält fachliche Fehler."},
        {value: 1, label: "Der Text ist für die Zielgruppe nicht geeignet."},
        {value: 2, label: "Die KI-Funktion hat zu lange geladen."},
        {value: "custom", label: "Freitext"},
      ],
    },
    required: {
      type: Boolean,
      default: false,
    },
  });

  const rating = defineModel("rating", {type: String, default: null});
  const reason = defineModel("reason", {type: String, default: null});

  const selectReason = ref(null);

  watch(selectReason, (newValue) => {
    if (newValue !== "custom") {
      const selectOptions = rating.value === "up" ? props.upOptions : props.downOptions;
      const selectedOption = selectOptions.find((option) => option.value === newValue);
      reason.value = selectedOption ? selectedOption.label : null;
    }
  });

  // derive the dropdown selection from the reason, e.g. when a stored rating is passed in;
  // a reason that matches no option falls back to the free-text ("custom") entry
  watch(
    [rating, reason],
    () => {
      if (!reason.value) {
        selectReason.value = null;
        return;
      }
      const selectOptions = rating.value === "up" ? props.upOptions : props.downOptions;
      const matched = selectOptions.find((option) => option.label === reason.value);
      selectReason.value = matched ? matched.value : "custom";
    },
    {immediate: true},
  );

  const setRating = (value) => {
    rating.value = value;
    selectReason.value = null;
    reason.value = null;
  };
</script>

<template>
  <div class="mt-3">
    <div class="font-bold">
      {{ label }}
      <template v-if="required">*</template>
    </div>

    <span class="mt-1 flex items-center space-x-3">
      <MedButton
        variant="primary"
        :flat="rating !== 'up'"
        rounded="full"
        icon="thumb_up"
        :disabled="disabled"
        :data-test="getTestId('rating-up-button')"
        @click="setRating('up')"
      />

      <MedButton
        variant="primary"
        :flat="rating !== 'down'"
        rounded="full"
        icon="thumb_down"
        :disabled="disabled"
        :data-test="getTestId('rating-down-button')"
        @click="setRating('down')"
      />

      <template v-if="rating !== null">
        <ComponentSelect
          v-model="selectReason"
          :options="rating === 'up' ? upOptions : downOptions"
          :placeholder-text="'Bitte Grund auswählen' + (required ? ' *' : '')"
          key-name="label"
          key-value="value"
          :full-width="false"
        />
      </template>

      <span v-if="required" class="text-sm text-gray-500">* Pflichtangaben</span>
    </span>

    <template v-if="rating !== null && selectReason === 'custom'">
      <ComponentTextarea
        v-model="reason"
        class="mt-2"
        rows="3"
        placeholder="Nennen Sie uns Ihren Grund. Geben Sie keine privaten oder sensiblen Informationen an."
      />
    </template>
  </div>
</template>
